import React, { useEffect, useState } from "react";
import { api, extractApiError } from "@/lib/api";
import { useT, useLanguage } from "@/lib/LanguageContext";
import { formatDateTime } from "../lib/dateTime";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { PageLoadError } from "./NoAccess";
import { useScreenWrites } from "./screenWrites";
import { Loader2, Plus, Trash2, Send, Calendar, Facebook, Instagram, Linkedin, Twitter } from "lucide-react";

const ICONS = { facebook: Facebook, instagram: Instagram, linkedin: Linkedin, twitter: Twitter };
const PLATFORMS = ["facebook", "instagram", "linkedin", "twitter"];
// Every status `super_admin_hq_routes.create_post` writes; each has an `hsoc.status.<value>` label.
export const SOCIAL_POST_STATUSES = ["scheduled", "published"];

export default function HqSocial() {
  const t = useT("superadmin");
  const { lang } = useLanguage();
  // Reading is tenants.read; connecting, disconnecting, posting and deleting are settings.write.
  const { manage: canWrite } = useScreenWrites("social");
  const [accounts, setAccounts] = useState([]);
  const [posts, setPosts] = useState([]);
  const [showAddAccount, setShowAddAccount] = useState(false);
  const [showComposer, setShowComposer] = useState(false);

  const [loadError, setLoadError] = useState(null);

  const load = async () => {
    setLoadError(null);
    try {
      const [a, p] = await Promise.all([
        api.get("/super-admin/hq/social/accounts"),
        api.get("/super-admin/hq/social/posts"),
      ]);
      setAccounts(a.data); setPosts(p.data);
    } catch (e) { setLoadError(e); }
  };
  const remove = async (path, doneKey) => {
    try {
      await api.delete(path);
    } catch (e) { toast.error(extractApiError(e, t("hsoc.error"), t)); return; }
    if (doneKey) toast.success(t(doneKey));
    load();
  };
  useEffect(() => { load(); }, []);

  if (loadError) {
    return (
      <div data-testid="hq-social">
        <h1 className="font-sans font-semibold text-5xl mb-8 text-slate-800">{t("hsoc.title")}</h1>
        <PageLoadError error={loadError} onRetry={load} testId="hq-social-load-error" />
      </div>
    );
  }

  return (
    <div data-testid="hq-social">
      <div className="flex items-start justify-between mb-8">
        <div>
          <h1 className="font-sans font-semibold text-5xl mb-2 text-slate-800">{t("hsoc.title")}</h1>
          <p className="text-slate-500">{t("hsoc.subtitle")}</p>
        </div>
        {canWrite && (
          <Button onClick={() => setShowComposer(true)} className="bg-sol-gold text-black hover:bg-amber-200" data-testid="new-post">
            <Send className="w-4 h-4 mr-2" /> {t("hsoc.newPost")}
          </Button>
        )}
      </div>

      {/* Connected accounts */}
      <div className="rounded-2xl bg-white border border-slate-200 p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-sans font-semibold text-xl">{t("hsoc.connectedAccounts")}</h3>
          {canWrite && (
            <Button onClick={() => setShowAddAccount(true)} size="sm" variant="outline" className="border-slate-200 text-slate-800/70" data-testid="add-account">
              <Plus className="w-4 h-4 mr-2" /> {t("hsoc.connect")}
            </Button>
          )}
        </div>
        {accounts.length === 0 ? (
          <p className="text-slate-400 text-sm py-6 text-center">{t("hsoc.noAccounts")}</p>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {accounts.map((a) => {
              const Icon = ICONS[a.platform] || Send;
              return (
                <div key={a.account_id} className="rounded-xl bg-white border border-slate-200 p-4 flex items-center gap-3" data-testid={`account-${a.account_id}`}>
                  <Icon className="w-5 h-5 text-amber-600" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm capitalize">{a.platform}</div>
                    <div className="text-xs text-slate-500 truncate">@{a.handle}</div>
                  </div>
                  {canWrite && <button
                    onClick={async () => {
                      if (!window.confirm(t("hsoc.confirmDisconnect"))) return;
                      await remove(`/super-admin/hq/social/accounts/${a.account_id}`, "hsoc.disconnected");
                    }}
                    className="text-rose-400 hover:text-rose-600" data-testid={`disconnect-${a.account_id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Posts */}
      <div className="rounded-2xl bg-white border border-slate-200 p-6">
        <h3 className="font-sans font-semibold text-xl mb-4">{t("hsoc.publications")}</h3>
        {posts.length === 0 ? <p className="text-slate-400 text-sm py-6 text-center">{t("hsoc.noPosts")}</p> : (
          <div className="space-y-3">
            {posts.map((p) => (
              <div key={p.post_id} className="rounded-xl bg-white border border-slate-100 p-4" data-testid={`post-${p.post_id}`}>
                <div className="flex items-start gap-3">
                  <div className="flex gap-1.5 mt-0.5">
                    {(p.platforms || []).map((pl) => {
                      const I = ICONS[pl] || Send;
                      return <I key={pl} className="w-3.5 h-3.5 text-amber-600" />;
                    })}
                  </div>
                  <div className="flex-1">
                    <div className="text-sm text-slate-700 whitespace-pre-wrap">{p.body}</div>
                    {p.media_url && <img src={p.media_url} alt="" className="mt-2 max-h-32 rounded" />}
                    <div className="text-xs text-slate-400 mt-2 flex items-center gap-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs ${p.status === "published" ? "bg-emerald-100 text-emerald-600" : "bg-amber-100 text-amber-600"}`} data-testid={`post-status-${p.post_id}`}>
                        {/* A value outside the vocabulary shows raw rather than as a blank pill. */}
                        {SOCIAL_POST_STATUSES.includes(p.status) ? t(`hsoc.status.${p.status}`) : p.status}
                      </span>
                      {p.scheduled_at && <><Calendar className="w-3 h-3" />{formatDateTime(p.scheduled_at, lang)}</>}
                      <span>· {p.created_by}</span>
                      {p.demo_mode && <span className="text-amber-600">⚡ {t("hsoc.demo")}</span>}
                    </div>
                  </div>
                  {canWrite && <button
                    onClick={async () => {
                      if (!window.confirm(t("hsoc.confirmDeletePost"))) return;
                      await remove(`/super-admin/hq/social/posts/${p.post_id}`);
                    }}
                    className="text-rose-400 hover:text-rose-600" data-testid={`delete-post-${p.post_id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {canWrite && showAddAccount && <AddAccountModal t={t} onClose={() => setShowAddAccount(false)} onDone={() => { setShowAddAccount(false); load(); }} />}
      {canWrite && showComposer && <ComposerModal t={t} accounts={accounts} onClose={() => setShowComposer(false)} onDone={() => { setShowComposer(false); load(); }} />}
    </div>
  );
}

function AddAccountModal({ t, onClose, onDone }) {
  const [form, setForm] = useState({ platform: "facebook", handle: "solunary.health", page_id: "", access_token: "" });
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/super-admin/hq/social/accounts", form);
      toast.success(t("hsoc.accountConnected")); onDone();
    } catch (err) { toast.error(extractApiError(err, t("hsoc.error"), t)); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white border border-slate-200 rounded-2xl p-6 max-w-md w-full" data-testid="add-account-modal">
        <h3 className="font-sans text-xl mb-4">{t("hsoc.connectAccount")}</h3>
        <select value={form.platform} onChange={(e) => setForm({ ...form, platform: e.target.value })} className="w-full bg-white border border-slate-200 rounded-md px-3 py-2 text-sm mb-3" data-testid="account-platform">
          {PLATFORMS.map((p) => <option key={p} value={p} className="bg-white">{p}</option>)}
        </select>
        <Input required placeholder={t("hsoc.phHandle")} value={form.handle} onChange={(e) => setForm({ ...form, handle: e.target.value })} className="bg-white border-slate-200 mb-3" data-testid="account-handle" />
        <Input placeholder={t("hsoc.phPageId")} value={form.page_id} onChange={(e) => setForm({ ...form, page_id: e.target.value })} className="bg-white border-slate-200 mb-3" />
        <Input type="password" placeholder={t("hsoc.phAccessToken")} value={form.access_token} onChange={(e) => setForm({ ...form, access_token: e.target.value })} className="bg-white border-slate-200 mb-4" />
        <Button type="submit" className="w-full bg-sol-gold text-black hover:bg-amber-200" data-testid="submit-account">{t("hsoc.connect")}</Button>
      </form>
    </div>
  );
}

function ComposerModal({ t, accounts, onClose, onDone }) {
  const [form, setForm] = useState({ platforms: [], body: "", media_url: "", scheduled_at: "" });
  const availablePlatforms = [...new Set(accounts.map((a) => a.platform))];
  const submit = async (e) => {
    e.preventDefault();
    if (form.platforms.length === 0) { toast.error(t("hsoc.choosePlatform")); return; }
    try {
      const payload = { ...form, scheduled_at: form.scheduled_at || null };
      await api.post("/super-admin/hq/social/posts", payload);
      toast.success(payload.scheduled_at ? t("hsoc.postScheduled") : t("hsoc.postPublished")); onDone();
    } catch (err) { toast.error(extractApiError(err, t("hsoc.error"), t)); }
  };
  const togglePlatform = (p) => {
    setForm({ ...form, platforms: form.platforms.includes(p) ? form.platforms.filter((x) => x !== p) : [...form.platforms, p] });
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white border border-slate-200 rounded-2xl p-6 max-w-lg w-full" data-testid="composer-modal">
        <h3 className="font-sans text-xl mb-4">{t("hsoc.newPost")}</h3>

        <div className="text-xs text-slate-500 mb-2">{t("hsoc.platforms")}</div>
        <div className="flex gap-2 mb-4 flex-wrap">
          {availablePlatforms.length === 0 ? <span className="text-xs text-rose-600">{t("hsoc.connectFirst")}</span> : availablePlatforms.map((p) => {
            const Icon = ICONS[p];
            return (
              <button type="button" key={p} onClick={() => togglePlatform(p)} data-testid={`compose-${p}`}
                className={`px-3 py-1.5 rounded-lg text-xs inline-flex items-center gap-1.5 border ${form.platforms.includes(p) ? "bg-sol-gold/15 border-sol-gold/30 text-amber-600" : "border-slate-200 text-slate-500"}`}>
                <Icon className="w-3.5 h-3.5" /> {p}
              </button>
            );
          })}
        </div>

        <textarea required rows={5} placeholder={t("hsoc.phBody")} value={form.body}
          onChange={(e) => setForm({ ...form, body: e.target.value })}
          className="w-full bg-white border border-slate-200 rounded-md px-3 py-2 text-sm mb-3" data-testid="composer-body" />
        <Input placeholder={t("hsoc.phMediaUrl")} value={form.media_url} onChange={(e) => setForm({ ...form, media_url: e.target.value })}
          className="bg-white border-slate-200 mb-3" />
        <div className="mb-4">
          <label className="text-xs text-slate-500 mb-1 block">{t("hsoc.scheduleLabel")}</label>
          <Input type="datetime-local" value={form.scheduled_at} onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })}
            className="bg-white border-slate-200" data-testid="composer-schedule" />
        </div>
        <Button type="submit" className="w-full bg-sol-gold text-black hover:bg-amber-200" data-testid="submit-post">
          {form.scheduled_at ? t("hsoc.schedule") : t("hsoc.publish")}
        </Button>
      </form>
    </div>
  );
}
